import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { catchError, mergeMap } from 'rxjs';
import { ErrorService } from './error.service';

export type Tenant = {
  id: string;
  distributor_id: string;
  franchise_id?: string;
  account_number: string;
  stripe_customer_id?: string;
  avatax_customer_code?: string;
  autopay_enabled: boolean;
  ignore_api_invoices: boolean;
  notes?: string;
  first: string;
  last: string;
  title: string;
  company: string;
  address: string;
  address2: string;
  city: string;
  st: string;
  zip: string;
  billing_address: string;
  billing_address2: string;
  billing_city: string;
  billing_st: string;
  billing_zip: string;
  phone1: string;
  phone2: string;
  emails: string[];
  billing_emails: string[];
  created_at: string;
};

export type TenantResponse = {
  tenant: Tenant;
};

export type TenantsResponse = {
  total_count: number;
  tenants: Tenant[];
};

export type BasicTenantInfoResponse = {
  tenant: Tenant;
  has_close_to_expiration: boolean;
  has_api_subscription: boolean;
  has_pending_api_invoice: boolean;
};

export type NewTenantRequest = {
  account_number?: string;
  avatax_customer_code?: string;
  distributor_id: string;
  franchise_id?: string | null;
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  st: string;
  zip: string;
  billing_address: string;
  billing_city: string;
  billing_st: string;
  billing_zip: string;
  send_invite: boolean;
};

export type UpdateTenantRequest = {
  first: string;
  last: string;
  title: string;
  company: string;
  address: string;
  address2: string;
  city: string;
  st: string;
  zip: string;
  billing_address: string;
  billing_address2: string;
  billing_city: string;
  billing_st: string;
  billing_zip: string;
  phone1: string;
  phone2: string;
  emails: string[];
  billing_emails: string[];
};

export type UpdateTenantResponse = {
  tenant: Tenant;
  qbo_customer?: any;
  qbo_error?: string;
};

export type AddTenantToFranchiseRequest = {
  tenant_id: string;
  franchise_id: string;
};

export type TenantSearchQuery = {
  account_number?: string;
  company_name?: string;
};

export type RecentTenantAccess = {
  id: string;
  tenant_id: string;
  user_id: string;
  latest_access_time: string;
  account_number: string;
  company: string;
};

export type RecentTenantsResponse = {
  recent_tenants: RecentTenantAccess[];
};

@Injectable({
  providedIn: 'root',
})
export class TenantsService {
  createTenantUrl = environment.serverUrl + '/tenants/create';
  getTenantUrl = environment.serverUrl + '/tenants/get';
  getCurrentTenantUrl = environment.serverUrl + '/tenants/get-current-tenant';
  getBasicTenantInfoUrl = environment.serverUrl + '/tenants/get-basic-info';
  getRangeUrl = environment.serverUrl + '/tenants/get-range';
  verifyAndInviteUserUrl = environment.serverUrl + '/tenants/verify-and-invite';
  searchTenantsByAccountUrl =
    environment.serverUrl + '/tenants/search-by-account';
  searchTenantsUrl = environment.serverUrl + '/tenants/search';
  updateTenantUrl = environment.serverUrl + '/tenants/update';
  updateTenantAutopayUrl = environment.serverUrl + '/tenants/update-autopay';
  updateTenantAdminValuesUrl =
    environment.serverUrl + '/tenants/update-admin-values';
  addTenantToFranchiseUrl = environment.serverUrl + '/tenants/add-to-franchise';
  removeTenantFromFranchiseUrl =
    environment.serverUrl + '/tenants/remove-from-franchise';
  getRecentTenantsUrl = environment.serverUrl + '/tenants/get-recent';

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private errorService: ErrorService
  ) {}

  createTenant(req: NewTenantRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        if (!req.franchise_id) {
          delete req.franchise_id;
        }
        return this.http.post<TenantResponse>(this.createTenantUrl, req, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        });
      }),
      catchError(this.errorService.handleObservableError<TenantResponse>())
    );
  }

  getTenantRange(offset: number, limit: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<TenantsResponse>(
          `${this.getRangeUrl}/${offset}/${limit}`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getTenantById(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<TenantResponse>(
          this.getTenantUrl + '/' + tenantId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getCurrentTenantDetails() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<TenantResponse>(this.getCurrentTenantUrl, {
          headers: { Authorization: 'Bearer ' + token },
        });
      })
    );
  }

  getBasicTenantInfo(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<BasicTenantInfoResponse>(
          this.getBasicTenantInfoUrl + '/' + tenantId,
          {
            headers: { Authorization: 'Bearer ' + token },
          }
        );
      })
    );
  }

  updateTenant(tenantId: string, update: UpdateTenantRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<UpdateTenantResponse>(
          this.updateTenantUrl + '/' + tenantId,
          update,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateTenantAutopay(tenantId: string, autopay: boolean) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<{ message: string }>(
          `${this.updateTenantAutopayUrl}/${tenantId}/${autopay}`,
          {
            autopay: autopay,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateTenantAdminValues(
    tenantId: string,
    avataxCustomerCode?: string,
    notes?: string,
    autopayEnabled?: boolean,
    ignoreApiInvoices?: boolean
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<UpdateTenantResponse>(
          this.updateTenantAdminValuesUrl,
          {
            tenant_id: tenantId,
            avatax_customer_code: avataxCustomerCode,
            notes: notes,
            autopay_enabled: autopayEnabled,
            ignore_api_invoices: ignoreApiInvoices,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      }),
      catchError(
        this.errorService.handleObservableError<UpdateTenantResponse>()
      )
    );
  }

  searchTenantsByAccountNumber(query: string, offset: number, limit: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<TenantsResponse>(
          `${this.searchTenantsByAccountUrl}/${query}/${offset}/${limit}`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  searchTenants(query: TenantSearchQuery) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<TenantsResponse>(this.searchTenantsUrl, query, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  addTenantToFranchise(tenantId: string, franchiseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<TenantResponse>(
          this.addTenantToFranchiseUrl,
          { tenant_id: tenantId, franchise_id: franchiseId },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  removeTenantFromFranchise(tenantId: string, franchiseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<TenantResponse>(
          this.removeTenantFromFranchiseUrl,
          { tenant_id: tenantId, franchise_id: franchiseId },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  verifyAndInviteUser(accountNumber: string, email: string, zipcode: string) {
    return this.http.post<TenantResponse>(this.verifyAndInviteUserUrl, {
      account_number: accountNumber,
      email: email,
      zip: zipcode,
    });
  }

  tenantAddressValid(tenant: Tenant) {
    return (
      tenant.address.trim() !== '' &&
      tenant.city.trim() !== '' &&
      tenant.st.trim() !== '' &&
      tenant.zip.trim() !== ''
    );
  }

  validateAccountNumber(accountNumber: string) {
    return /^(77|88|99)\d{6}01$/.test(accountNumber);
  }

  getRecentTenants() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<RecentTenantsResponse>(this.getRecentTenantsUrl, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }
}
